/**
 * Web Push subscription utilities.
 *
 * Note: The actual push event handler (showing notifications) lives in the
 * service worker. With vite-plugin-pwa you can extend the service worker via
 * the `injectManifest` strategy or an `additionalManifestEntries` hook. The
 * service worker needs a `push` event listener such as:
 *
 *   self.addEventListener('push', (event) => {
 *     const data = event.data?.json() ?? {}
 *     event.waitUntil(
 *       self.registration.showNotification(data.title ?? 'IntelHub Alert', {
 *         body: data.body,
 *         icon: '/icons/icon-192.png',
 *         badge: '/icons/icon-192.png',
 *         data: { url: data.url },
 *       })
 *     )
 *   })
 */

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? ''

/**
 * Convert a URL-safe base64 VAPID public key to Uint8Array
 * as required by PushManager.subscribe.
 */
export function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray.buffer as ArrayBuffer
}

/** Returns true if the browser supports Web Push */
export function isPushSupported(): boolean {
  return 'PushManager' in window && 'serviceWorker' in navigator
}

/**
 * Request notification permission and subscribe to push notifications.
 * Returns the PushSubscription on success, or null if denied / unsupported.
 */
export async function subscribeToPushNotifications(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null

  if (Notification.permission === 'denied') return null

  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') return null
  }

  try {
    const registration = await navigator.serviceWorker.ready
    const existingSubscription = await registration.pushManager.getSubscription()
    if (existingSubscription) return existingSubscription

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    })

    return subscription
  } catch (err) {
    console.error('[push] Failed to subscribe:', err)
    return null
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported()) return

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await subscription.unsubscribe()
    }
  } catch (err) {
    console.error('[push] Failed to unsubscribe:', err)
  }
}
